import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { Client } from '../../../../context/ClientContext';
import { Badge, Dropdown, Input, SegmentedControl, StatCard, Table, TableColumn, useToast } from '../../../ui';
import { supabase } from '../../../../lib/supabase';
import BacklinkDetailPanel from './BacklinkDetailPanel';
import { faint, fmtCompact, formatRelative, linkStyle, urlPath } from './format';
import { CountLabel, DRBadge } from './shared';

type Backlink = {
  id: string;
  source_url: string;
  target_url: string;
  anchor_text: string | null;
  link_type: string | null;
  source_domain: string;
  source_dr: number | null;
  source_ur: number | null;
  source_traffic: number | null;
  first_seen: string | null;
  last_seen: string | null;
  is_live: boolean;
  hidden: boolean;
  hidden_reason: string | null;
  notes: string | null;
  status: string | null;
  created_at: string;
};

type Filter = 'all' | 'dofollow' | 'high_authority' | 'new' | 'lost' | 'hidden';

const NEW_DAYS = 30;
const isNew = (r: Backlink) => !!r.first_seen && (Date.now() - new Date(r.first_seen).getTime()) / 86400000 <= NEW_DAYS;

/** Each filter's test. Every filter but Hidden leaves hidden links out. */
const FILTERS: Record<Filter, (r: Backlink) => boolean> = {
  all: (r) => !r.hidden,
  dofollow: (r) => !r.hidden && r.link_type === 'dofollow',
  high_authority: (r) => !r.hidden && r.source_dr != null && r.source_dr >= 50,
  new: (r) => !r.hidden && isNew(r),
  lost: (r) => !r.hidden && !r.is_live,
  hidden: (r) => r.hidden,
};

const MIN_DR_OPTIONS = [
  { value: '0', label: 'Any DR' },
  { value: '20', label: 'DR 20+' },
  { value: '30', label: 'DR 30+' },
  { value: '50', label: 'DR 50+' },
  { value: '70', label: 'DR 70+' },
];

export default function BacklinksInventory({ client }: { client: Client }) {
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [rows, setRows] = useState<Backlink[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [minDr, setMinDr] = useState(0);

  const selectedId = searchParams.get('backlink');
  const setSelectedId = (id: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (id) next.set('backlink', id);
    else next.delete('backlink');
    setSearchParams(next);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('client_backlinks')
        .select('*')
        .eq('client_id', client.id)
        .order('source_dr', { ascending: false, nullsFirst: false })
        .order('created_at', { ascending: false });

      if (error) throw error;
      setRows(data || []);
    } catch (err) {
      console.error('Error loading backlinks:', err);
      toast.error('Failed to load backlinks', err instanceof Error ? err.message : undefined);
      setRows([]); // Set empty array on error to avoid undefined issues
    } finally {
      setLoading(false);
    }
  }, [client.id, toast]);

  useEffect(() => {
    load();
  }, [load]);

  const counts = useMemo(
    () => Object.fromEntries((Object.keys(FILTERS) as Filter[]).map((f) => [f, rows.filter(FILTERS[f]).length])) as Record<Filter, number>,
    [rows],
  );

  const stats = useMemo(() => {
    const live = rows.filter((r) => !r.hidden && r.is_live);
    const dofollow = live.filter((r) => r.link_type === 'dofollow').length;
    return {
      domains: new Set(live.map((r) => r.source_domain)).size,
      live: live.length,
      dofollowPct: live.length ? Math.round((dofollow / live.length) * 100) : 0,
      newCount: live.filter(isNew).length,
      lost: counts.lost,
    };
  }, [rows, counts.lost]);

  const filtered = rows.filter((row) => {
    if (!FILTERS[filter](row)) return false;
    if (minDr && (row.source_dr == null || row.source_dr < minDr)) return false;
    if (search) {
      const q = search.toLowerCase();
      if (
        !row.source_domain.toLowerCase().includes(q) &&
        !row.target_url.toLowerCase().includes(q) &&
        !(row.anchor_text || '').toLowerCase().includes(q)
      ) {
        return false;
      }
    }
    return true;
  });

  const handleHide = async (id: string, reason: string) => {
    try {
      const { error } = await supabase.from('client_backlinks').update({ hidden: true, hidden_reason: reason }).eq('id', id);
      if (error) throw error;
      toast.success('Link hidden');
      load();
    } catch (err) {
      console.error('Error hiding link:', err);
      toast.error('Failed to hide link', err instanceof Error ? err.message : undefined);
    }
  };

  const handleUnhide = async (id: string) => {
    try {
      const { error } = await supabase.from('client_backlinks').update({ hidden: false, hidden_reason: null }).eq('id', id);
      if (error) throw error;
      toast.success('Link unhidden');
      load();
    } catch (err) {
      console.error('Error unhiding link:', err);
      toast.error('Failed to unhide link', err instanceof Error ? err.message : undefined);
    }
  };

  const columns: TableColumn<Backlink>[] = [
    {
      key: 'source_domain',
      label: 'Source',
      width: '24%',
      render: (_value, row) => (
        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <a
            href={row.source_url}
            target="_blank"
            rel="noopener noreferrer"
            style={{ ...linkStyle, fontWeight: 'var(--weight-medium)' as React.CSSProperties['fontWeight'], overflow: 'hidden', textOverflow: 'ellipsis' }}
            onClick={(e) => e.stopPropagation()}
          >
            {row.source_domain}
          </a>
          <span style={{ ...faint, fontSize: 'var(--text-xs)', overflow: 'hidden', textOverflow: 'ellipsis' }} title={row.source_url}>
            {urlPath(row.source_url)}
          </span>
        </div>
      ),
    },
    { key: 'source_dr', label: 'DR', width: '7%', align: 'center', render: (_value, row) => <DRBadge dr={row.source_dr} /> },
    {
      key: 'anchor_text',
      label: 'Anchor',
      width: '22%',
      render: (_value, row) => (
        <span
          style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', ...(row.anchor_text ? {} : faint) }}
          title={row.anchor_text || undefined}
        >
          {row.anchor_text || 'No anchor'}
        </span>
      ),
    },
    {
      key: 'target_url',
      label: 'Links to',
      width: '19%',
      render: (_value, row) => (
        <span className="t-num" style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', color: 'var(--text-muted)', fontSize: 'var(--text-sm)' }} title={row.target_url}>
          {urlPath(row.target_url)}
        </span>
      ),
    },
    {
      key: 'link_type',
      label: 'Type',
      width: '10%',
      render: (_value, row) =>
        row.link_type === 'dofollow' ? <Badge tone="up">Dofollow</Badge> : <Badge tone="neutral">{row.link_type || 'Unknown'}</Badge>,
    },
    {
      key: 'source_traffic',
      label: 'Traffic',
      width: '8%',
      align: 'right',
      render: (_value, row) => (
        <span className="t-num" style={row.source_traffic == null ? faint : { color: 'var(--text-muted)' }}>
          {fmtCompact(row.source_traffic)}
        </span>
      ),
    },
    {
      key: 'first_seen',
      label: 'Seen',
      width: '10%',
      align: 'right',
      render: (_value, row) =>
        !row.is_live ? (
          <Badge tone="down">Lost</Badge>
        ) : (
          <span style={{ color: 'var(--text-muted)', fontSize: 'var(--text-sm)' }}>{row.first_seen ? formatRelative(row.first_seen) : '—'}</span>
        ),
    },
  ];

  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 'var(--space-3)', marginBottom: 'var(--space-5)' }}>
        <StatCard label="Referring domains" value={stats.domains.toLocaleString()} hint={`${stats.live.toLocaleString()} live links`} />
        <StatCard label="Dofollow" value={`${stats.dofollowPct}%`} hint="of live links" />
        <StatCard label={`New · ${NEW_DAYS}d`} value={stats.newCount.toLocaleString()} />
        <StatCard label="Lost" value={stats.lost.toLocaleString()} hint={stats.lost ? 'Worth a reclaim email' : 'None lost'} />
      </div>

      <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center', flexWrap: 'wrap', marginBottom: 'var(--space-4)' }}>
        <SegmentedControl
          value={filter}
          onChange={(id) => setFilter(id as Filter)}
          options={[
            { id: 'all', label: <CountLabel label="All" n={counts.all} /> },
            { id: 'dofollow', label: <CountLabel label="Dofollow" n={counts.dofollow} /> },
            { id: 'high_authority', label: <CountLabel label="DR 50+" n={counts.high_authority} /> },
            { id: 'new', label: <CountLabel label="New" n={counts.new} /> },
            { id: 'lost', label: <CountLabel label="Lost" n={counts.lost} /> },
            { id: 'hidden', label: <CountLabel label="Hidden" n={counts.hidden} /> },
          ]}
          style={{ maxWidth: '100%', overflowX: 'auto' }}
        />
        <span style={{ flex: 1 }} />
        <div style={{ width: '260px' }}>
          <Input icon="search" fieldSize="sm" placeholder="Search domain, anchor, page" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <Dropdown size="sm" options={MIN_DR_OPTIONS} value={String(minDr)} onChange={(e) => setMinDr(Number(e.target.value))} style={{ width: '120px' }} />
      </div>

      <Table
        rows={filtered}
        columns={columns}
        emptyMessage={
          <span style={faint}>
            {loading ? 'Loading…' : rows.length ? 'No backlinks match these filters.' : 'No backlinks yet. They appear here once a sync pushes them in.'}
          </span>
        }
        onRowClick={(row) => setSelectedId(row.id)}
        rowStyle={(row) => (row.is_live && !row.hidden ? undefined : { opacity: 0.6 })}
      />

      {selectedId && (
        <BacklinkDetailPanel backlinkId={selectedId} onClose={() => setSelectedId(null)} onUpdate={load} onHide={handleHide} onUnhide={handleUnhide} />
      )}
    </>
  );
}
